import React, { useState, useContext, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { DataContext } from '../contexts/DataContext';
import { Package, ShoppingCart, History, FileText, Settings, LogOut, ShoppingBag, Menu, X, DollarSign, User, BarChart3, LayoutDashboard, Users, ChevronDown, ChevronRight } from 'lucide-react';
import { buildSellerScope, normalizeSellerKey } from '../utils/sellerIdentity';

const Sidebar = ({ isScrolled }) => {
    const { onlineOrders, systemUser, logoutSystem, settings, orderBoardSummary } = useContext(DataContext);
    const location = useLocation();
    const normalizedPath = String(location.pathname || '').trim().toLowerCase();
    const [isSalesOpen, setIsSalesOpen] = useState(false);
    const [isFinancialOpen, setIsFinancialOpen] = useState(() => {
        try {
            const saved = localStorage.getItem('doutor_financial_open');
            if (saved === '1') return true;
        } catch {}
        return false;
    });
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const isJunior = String(systemUser?.username || '').trim().toLowerCase() === 'junior';
    const rayaneUsername = String(systemUser?.username || '').trim().toLowerCase();
    const rayaneName = String(systemUser?.name || '').trim().toLowerCase();
    const isRayaneFranchiseUser = rayaneUsername === 'rayane' || rayaneUsername === 'rayane_master' || rayaneName === 'rayane';
    const duduUsername = String(systemUser?.username || '').trim().toLowerCase();
    const duduName = String(systemUser?.name || '').trim().toLowerCase();
    const duduRole = String(systemUser?.role || '').trim().toLowerCase();
    const hasDuduFranchiseExtras = duduUsername === 'dudu' || duduName === 'dudu' || duduName === 'eduardo';
    const isDuduFranchiseUser = duduRole === 'franchise_manager' || duduRole === 'franchise_analyst';
    const isScopedFranchiseUser = isRayaneFranchiseUser || isDuduFranchiseUser;
    const isFranchiseReporter = (() => {
        const role = String(systemUser?.role || '').trim().toLowerCase();
        return role === 'admin' || isRayaneFranchiseUser || hasDuduFranchiseExtras;
    })();
    const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(() => {
        try {
            return localStorage.getItem('doutor_sidebar_collapsed') === '1';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 900;
            setIsMobile(mobile);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isMobile) {
            setIsDesktopCollapsed(true);
            return;
        }
        try {
            const persisted = localStorage.getItem('doutor_sidebar_collapsed');
            if (persisted === '1') setIsDesktopCollapsed(true);
            else setIsDesktopCollapsed(false);
        } catch {
            setIsDesktopCollapsed(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    const localPendingOrdersCount = onlineOrders.filter(o => {
        if (o?.archived) return false;
        const status = String(o?.status || '').toLowerCase();
        const paymentStatus = String(o?.paymentStatus || '').toLowerCase();
        const channel = String(o?.channel || '').toLowerCase();

        const isPendingLike =
            status === 'pending' ||
            status === 'novo' ||
            status === 'new' ||
            (!status && (paymentStatus === 'pending' || channel === 'whatsapp'));

        if (!isPendingLike) return false;

        // Store User: Hide Wholesale (Franquia)
        if (systemUser?.role === 'store' && (o.client?.type === 'wholesale' || o.client?.type === 'franchise')) {
            return false;
        }

        return true;
    }).length;
    const pendingOrdersCount = location.pathname === '/pedidos'
        && orderBoardSummary?.scope === `${systemUser?.role}:${systemUser?.username}`
        ? orderBoardSummary.pendingCount : localPendingOrdersCount;

    const tooltipTimerRef = useRef(null);
    const [navTooltip, setNavTooltip] = useState(null);

    const clearTooltipTimer = () => {
        if (tooltipTimerRef.current) {
            clearTimeout(tooltipTimerRef.current);
            tooltipTimerRef.current = null;
        }
    };

    const hideNavTooltip = () => {
        clearTooltipTimer();
        setNavTooltip(null);
    };

    const scheduleNavTooltip = (label, el) => {
        if (!isDesktopCollapsed || isMobile) return;
        clearTooltipTimer();
        tooltipTimerRef.current = setTimeout(() => {
            const rect = el.getBoundingClientRect();
            setNavTooltip({
                text: label,
                top: rect.top + rect.height / 2,
                left: rect.right + 12
            });
        }, 300);
    };

    const tooltipHandlers = (label) => ({
        onMouseEnter: (e) => scheduleNavTooltip(label, e.currentTarget),
        onMouseLeave: hideNavTooltip,
        onFocus: (e) => scheduleNavTooltip(label, e.currentTarget),
        onBlur: hideNavTooltip
    });

    const sellerMenuEnabled = (() => {
        if (!systemUser) return false;
        if (systemUser?.role === 'admin' || hasDuduFranchiseExtras) return true;
        const { matchedRecord } = buildSellerScope({
            systemUser,
            salesSellers: settings?.sales_sellers
        });
        return !!matchedRecord?.key;
    })();
    const isStockRestrictedSeller = (() => {
        if (!systemUser) return false;
        const role = normalizeSellerKey(systemUser?.role);
        if (role === 'admin' || role === 'supervisor') return false;
        if (isJunior || isScopedFranchiseUser || hasDuduFranchiseExtras) return false;
        if (role === 'store') return true;
        return sellerMenuEnabled;
    })();
    const normalizedUsernames = [
        normalizeSellerKey(systemUser?.username),
        normalizeSellerKey(systemUser?.name)
    ];
    const isPedroSeller = normalizedUsernames.some((value) => value === 'pedro' || value.split(/[\s._-]+/)[0] === 'pedro');
    const isFinancialRoute = [
        '/recebimentos',
        '/financeiro',
        '/financeiro-vendedor',
        '/cobrancas',
        '/cobranças',
        '/cobrancas-vendedor',
        '/minhas-vendas',
        '/minha-comissao',
        '/meus-inadimplentes'
    ].includes(normalizedPath);

    useEffect(() => {
        if (!isFinancialRoute) return;
        setIsFinancialOpen(true);
    }, [isFinancialRoute]);

    const navLinks = isJunior ? (
        <ul className="nav-links">
            <li>
                <NavLink
                    to="/gestao"
                    data-label="Painel"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Painel')}
                >
                    <LayoutDashboard size={20} />
                    <span>Painel</span>
                </NavLink>
            </li>
            <li>
                <NavLink
                    to="/vendedores"
                    data-label="Vendedores"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Vendedores')}
                >
                    <BarChart3 size={20} />
                    <span>Vendedores</span>
                </NavLink>
            </li>
            <li>
                <NavLink
                    to="/minhas-vendas"
                    data-label="Vendas dos vendedores"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Vendas dos vendedores')}
                >
                    <DollarSign size={20} />
                    <span>Vendas dos vendedores</span>
                </NavLink>
            </li>
            <li>
                <NavLink
                    to="/pedidos"
                    data-label="Pedidos Online"
                    className="nav-link-with-badge"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Pedidos Online')}
                >
                    <div className="nav-link-leading">
                        <ShoppingBag size={20} />
                        <span>Pedidos Online</span>
                    </div>
                    {pendingOrdersCount > 0 && (
                        <span className="pending-badge">
                            {pendingOrdersCount}
                        </span>
                    )}
                </NavLink>
            </li>
            <li>
                <NavLink
                    to="/recebimentos"
                    data-label="Financeiro"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Financeiro')}
                >
                    <DollarSign size={20} />
                    <span>Financeiro</span>
                </NavLink>
            </li>
            <li>
                <NavLink
                    to="/cobrancas"
                    data-label="Cobranças"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Cobranças')}
                >
                    <FileText size={20} />
                    <span>Cobranças</span>
                </NavLink>
            </li>
            <li>
                <NavLink
                    to="/config"
                    data-label="Configurações"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Configurações')}
                >
                    <Settings size={20} />
                    <span>Configurações</span>
                </NavLink>
            </li>
            {!isRayaneFranchiseUser && (
                <li>
                    <NavLink 
                        to="/historico"
                        data-label="Histórico"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Histórico')}
                    >
                        <History size={20} />
                        <span>Histórico</span>
                    </NavLink>
                </li>
            )}
            <li>
                <NavLink
                    to="/relatorios-junior"
                    data-label="Relatório Saídas"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Relatório Saídas')}
                >
                    <FileText size={20} />
                    <span>Relatório Saídas</span>
                </NavLink>
            </li>
        </ul>
    ) : (
        <ul className="nav-links">
            {systemUser?.role === 'admin' && !isDuduFranchiseUser && (
                <li>
                    <NavLink
                        to={isRayaneFranchiseUser ? '/relatorios-franquia' : '/gestao'}
                        data-label="Painel"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Painel')}
                    >
                        <LayoutDashboard size={20} />
                        <span>Painel</span>
                    </NavLink>
                </li>
            )}

            {!isStockRestrictedSeller && (
            <li>
                <NavLink 
                    to="/estoque"
                    data-label="Estoque"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Estoque')}
                >
                    <Package size={20} />
                    <span>Estoque</span>
                </NavLink>
            </li>
            )}

            <li>
                <NavLink 
                    to="/pedidos" 
                    data-label="Pedidos Online"
                    className="nav-link-with-badge"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Pedidos Online')}
                >
                    <div className="nav-link-leading">
                        <ShoppingBag size={20} />
                        <span>Pedidos Online</span>
                    </div>
                    {pendingOrdersCount > 0 && (
                        <span className="pending-badge">
                            {pendingOrdersCount}
                        </span>
                    )}
                </NavLink>
            </li>
            
            {/* Special Vendas Button */}
            {(systemUser?.role !== 'admin' || isScopedFranchiseUser) && (
            <li 
                className="nav-item-dropdown special-action-wrapper"
                onMouseEnter={() => setIsSalesOpen(true)}
                onMouseLeave={() => setIsSalesOpen(false)}
            >
                <NavLink 
                    to="/vendas" 
                    className={({ isActive }) => `special-action-btn ${isActive ? 'active' : ''}`}
                    data-label="Nova Venda"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Nova Venda')}
                >
                    <div className="special-btn-content">
                        <div className="icon-pulse">
                            <ShoppingCart size={22} />
                        </div>
                        <div className="text-content">
                            <span className="main-text">Nova Venda</span>
                            <span className="sub-text">PDV & Caixa</span>
                        </div>
                    </div>
                </NavLink>
            </li>
            )}

            {!isRayaneFranchiseUser && !isDuduFranchiseUser && (
                <li>
                    <NavLink 
                        to="/fechamento"
                        data-label="Fechamento de Caixa"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers(systemUser?.role === 'admin' ? 'Relatório Diário' : 'Fechamento de Caixa')}
                    >
                        <FileText size={20} />
                        <span>{systemUser?.role === 'admin' ? 'Relatório Diário' : 'Fechamento de Caixa'}</span>
                    </NavLink>
                </li>
            )}

            {!isRayaneFranchiseUser && !isDuduFranchiseUser && (
            <li>
                <NavLink 
                    to="/historico"
                    data-label="Histórico"
                    onClick={() => {
                        hideNavTooltip();
                        isMobile && setIsMobileMenuOpen(false);
                    }}
                    {...tooltipHandlers('Histórico')}
                >
                    <History size={20} />
                    <span>Histórico</span>
                </NavLink>
            </li>
            )}

            {(isRayaneFranchiseUser || isDuduFranchiseUser || hasDuduFranchiseExtras) && (
                <li>
                    <NavLink 
                        to="/historico-franquia"
                        data-label="Histórico Franquia"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Histórico Franquia')}
                    >
                        <History size={20} />
                        <span>Histórico Franquia</span>
                    </NavLink>
                </li>
            )}

            {isJunior && (
                <li>
                    <NavLink
                        to="/relatorios-junior"
                        data-label="Relatório Saídas"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Relatório Saídas')}
                    >
                        <FileText size={20} />
                        <span>Relatório Saídas</span>
                    </NavLink>
                </li>
            )}

            {isFranchiseReporter && (
                <li>
                    <NavLink
                        to="/relatorios-franquia"
                        data-label="Relatório Franquia"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Relatório Franquia')}
                    >
                        <FileText size={20} />
                        <span>Relatório Franquia</span>
                    </NavLink>
                </li>
            )}

            {(isRayaneFranchiseUser || isDuduFranchiseUser || hasDuduFranchiseExtras) && (
                <li>
                    <NavLink
                        to="/franqueados"
                        data-label="Franqueados"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Franqueados')}
                    >
                        <User size={20} />
                        <span>Franqueados</span>
                    </NavLink>
                </li>
            )}

            {!isRayaneFranchiseUser && !isDuduFranchiseUser && (
                <li>
                    <NavLink
                        to="/clientes"
                        data-label="Clientes"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Clientes')}
                    >
                        <User size={20} />
                        <span>Clientes</span>
                    </NavLink>
                </li>
            )}

            {(systemUser?.role === 'admin' || systemUser?.role === 'store') && !isScopedFranchiseUser && (
                <li>
                    <NavLink
                        to="/interior"
                        data-label="Interior"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Interior')}
                    >
                        <Users size={20} />
                        <span>Interior</span>
                    </NavLink>
                </li>
            )}
            
            {systemUser?.role === 'admin' && !isScopedFranchiseUser && (
                <li>
                    <NavLink 
                        to="/vendedores"
                        data-label="Vendedores"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Vendedores')}
                    >
                        <BarChart3 size={20} />
                        <span>Vendedores</span>
                    </NavLink>
                </li>
            )}
            {(systemUser?.role === 'admin' || sellerMenuEnabled) && !isScopedFranchiseUser && (
                <li>
                    <NavLink
                        to="/expedicao"
                        data-label="Expedição"
                        onClick={() => {
                            hideNavTooltip();
                            isMobile && setIsMobileMenuOpen(false);
                        }}
                        {...tooltipHandlers('Expedição')}
                    >
                        <Package size={20} />
                        <span>Expedição</span>
                    </NavLink>
                </li>
            )}
            {(((systemUser?.role === 'admin' && !isScopedFranchiseUser) || isJunior) || (sellerMenuEnabled && !isScopedFranchiseUser)) && (
                <li>
                    <button
                        type="button"
                        data-label="Financeiro"
                        className={`nav-parent-btn${isFinancialRoute ? ' active' : ''}`}
                        onClick={(e) => {
                            e?.preventDefault?.();
                            hideNavTooltip();
                            setIsFinancialOpen((v) => {
                                const next = !v;
                                try {
                                    localStorage.setItem('doutor_financial_open', next ? '1' : '0');
                                } catch {}
                                return next;
                            });
                        }}
                        {...tooltipHandlers('Financeiro')}
                    >
                        <DollarSign size={20} />
                        <span style={{ flex: 1 }}>Financeiro</span>
                        {isFinancialOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {isFinancialOpen && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: '0.25rem 8px 0.5rem 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {systemUser?.role === 'admin' && !isScopedFranchiseUser && (
                                <>
                                    <li>
                                        <NavLink
                                            to="/gestao-financeiro"
                                            data-label="Visão geral"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Visão geral')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Visão geral
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/recebimentos"
                                            data-label="Contas a receber"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Contas a receber')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Contas a receber
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/contas-a-pagar"
                                            data-label="Contas a pagar"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Contas a pagar')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Contas a pagar
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/caixa"
                                            data-label="Caixa"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Caixa')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Caixa
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/fluxo-de-caixa"
                                            data-label="Fluxo de caixa"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Fluxo de caixa')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Fluxo de caixa
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/inadimplencia"
                                            data-label="Inadimplência"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Inadimplência')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Inadimplência
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/comissoes"
                                            data-label="Comissões"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Comissões')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Comissões
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/conciliacao"
                                            data-label="Conciliação"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Conciliação')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Conciliação
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/relatorios-financeiro"
                                            data-label="Relatórios"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Relatórios')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Relatórios
                                        </NavLink>
                                    </li>
                                </>
                            )}
                            {isJunior && !isScopedFranchiseUser && (
                                <>
                                    <li>
                                        <NavLink
                                            to="/recebimentos"
                                            data-label="Visão geral"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Visão geral')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Visão geral
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/recebimentos"
                                            data-label="Contas a receber"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Contas a receber')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Contas a receber
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/contas-a-pagar"
                                            data-label="Contas a pagar"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Contas a pagar')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Contas a pagar
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/caixa"
                                            data-label="Caixa"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Caixa')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Caixa
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/fluxo-de-caixa"
                                            data-label="Fluxo de caixa"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Fluxo de caixa')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Fluxo de caixa
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/inadimplencia"
                                            data-label="Inadimplência"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Inadimplência')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Inadimplência
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/comissoes"
                                            data-label="Comissões"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Comissões')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Comissões
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/conciliacao"
                                            data-label="Conciliação"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Conciliação')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Conciliação
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/relatorios-financeiro"
                                            data-label="Relatórios"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Relatórios')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Relatórios
                                        </NavLink>
                                    </li>
                                </>
                            )}
                            {sellerMenuEnabled && !isScopedFranchiseUser && (
                                <>
                                    <li>
                                        <NavLink
                                            to="/minhas-vendas"
                                            data-label="Minhas vendas"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Minhas vendas')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Minhas vendas
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/financeiro-vendedor"
                                            data-label="Meus recebimentos"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Meus recebimentos')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Meus recebimentos
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/minha-comissao"
                                            data-label="Minha comissão"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Minha comissão')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Minha comissão
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/meus-inadimplentes"
                                            data-label="Meus inadimplentes"
                                            onClick={() => {
                                                hideNavTooltip();
                                                isMobile && setIsMobileMenuOpen(false);
                                            }}
                                            {...tooltipHandlers('Meus inadimplentes')}
                                            style={({ isActive }) => ({
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.45rem 0.7rem',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                color: isActive ? '#2C2F33' : '#6B7076',
                                                background: isActive ? '#EDEFF2' : 'transparent',
                                                fontWeight: isActive ? 700 : 500
                                            })}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3A3D42', display: 'inline-block' }} />
                                            Meus inadimplentes
                                        </NavLink>
                                    </li>
                                </>
                            )}
                        </ul>
                    )}
                </li>
            )}
            
            {systemUser?.role !== 'store' && !isPedroSeller && !isScopedFranchiseUser && (
                <>
                    <li>
                        <NavLink 
                            to="/sefaz"
                            data-label="Notas Fiscais"
                            onClick={() => {
                                hideNavTooltip();
                                isMobile && setIsMobileMenuOpen(false);
                            }}
                            {...tooltipHandlers('Notas Fiscais')}
                        >
                            <FileText size={20} />
                            <span>Notas Fiscais</span>
                        </NavLink>
                    </li>
                    <li>
                        <NavLink 
                            to="/config"
                            data-label="Configurações"
                            onClick={() => {
                                hideNavTooltip();
                                isMobile && setIsMobileMenuOpen(false);
                            }}
                            {...tooltipHandlers('Configurações')}
                        >
                            <Settings size={20} />
                            <span>Configurações</span>
                        </NavLink>
                    </li>
                </>
            )}
        </ul>
    );

    if (isMobile) {
        return (
            <>
                <nav className={`mobile-top-bar ${isScrolled ? 'scrolled' : ''}`}>
                    <button className="mobile-menu-trigger" onClick={() => setIsMobileMenuOpen(true)}>
                        <Menu size={24} />
                    </button>
                    
                    <div className="mobile-logo" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                         {/* Icon Removed */}
                         <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#fff', letterSpacing: '0.5px' }}>
                             Doutor Embalagens
                         </span>
                    </div>

                    <div style={{ width: '24px' }}></div>
                </nav>

                {isMobileMenuOpen && (
                    <div className="mobile-menu-overlay">
                         <div className="mobile-menu-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                {/* Icon Removed */}
                                <div>
                                    <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#fff', margin: 0 }}>Menu</h2>
                                    <p style={{ fontSize: '0.9rem', color: '#94a3b8', margin: 0 }}>
                                        {systemUser?.name || 'Usuário'}
                                    </p>
                                </div>
                            </div>
                            <button className="mobile-menu-close" onClick={() => setIsMobileMenuOpen(false)}>
                                <X size={28} />
                            </button>
                         </div>
                         
                         {navLinks}

                         <div className="mobile-logout-item">
                            <button className="logout-inline-btn" onClick={logoutSystem}>
                                <LogOut size={20} />
                                Sair do Sistema
                            </button>
                         </div>
                    </div>
                )}
            </>
        );
    }

    const sidebarClassName = [
        'sidebar',
        systemUser?.role === 'admin' ? 'admin-sidebar' : '',
        isDesktopCollapsed ? 'desktop-collapsed' : ''
    ].join(' ').trim();

    return (
        <div
            className={sidebarClassName}
        >
            <div className="sidebar-header">
                <div className="sidebar-header-row">
                    <p className="sidebar-header-label">
                        {isScopedFranchiseUser ? 'Painel Franquia' : (systemUser?.role === 'admin' ? 'Painel Administrativo' : 'Sistema de Gestão')}
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            if (isMobile) return;
                            setIsDesktopCollapsed(v => {
                                const next = !v;
                                try {
                                    localStorage.setItem('doutor_sidebar_collapsed', next ? '1' : '0');
                                } catch {}
                                return next;
                            });
                            hideNavTooltip();
                        }}
                        title={isDesktopCollapsed ? 'Expandir menu' : 'Recolher menu'}
                        className="sidebar-collapse-btn"
                    >
                        <Menu size={18} />
                    </button>
                </div>
            </div>
            
            {navLinks}

            <div className="sidebar-footer">
                <div className="user-avatar">
                    <span>{systemUser?.name?.charAt(0) || 'U'}</span>
                </div>
                <div className="user-info">
                    <h4>{systemUser?.name || 'Usuário'}</h4>
                    <p>{isScopedFranchiseUser ? 'Franquia' : (systemUser?.role === 'store' ? 'Loja' : systemUser?.role === 'admin' ? 'Admin' : 'Dev')}</p>
                </div>
                <button className="logout-btn" onClick={logoutSystem} title="Sair">
                    <LogOut size={18} />
                </button>
            </div>
            {isDesktopCollapsed && navTooltip && (
                <div
                    className="nav-tooltip-floating"
                    style={{ top: navTooltip.top, left: navTooltip.left }}
                >
                    {navTooltip.text}
                </div>
            )}
        </div>
    );
};

export default Sidebar;
